import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AlertsPanel = ({ title = "Smart Alerts" }) => {
  const [alerts, setAlerts] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    // Mock alerts data
    const mockAlerts = [
      {
        id: 1,
        type: 'engagement_spike',
        severity: 'high',
        title: 'Engagement Spike Detected',
        message: `Your Ooty tourism post is experiencing 300% higher engagement than usual. Consider boosting this content for maximum reach.`,
        timestamp: new Date(Date.now() - 300000),
        isRead: false,
        platform: 'instagram',
        metrics: { engagement: '+300%', reach: '45.2K' },
        action: 'Boost Post'
      },
      {
        id: 2,
        type: 'negative_sentiment',
        severity: 'medium',
        title: 'Negative Sentiment Alert',
        message: `Increased negative mentions detected for Kerala backwater tours. Recent comments mention booking process issues.`,
        timestamp: new Date(Date.now() - 900000),
        isRead: false,
        platform: 'twitter',
        metrics: { sentiment: '-15%', mentions: '23' },
        action: 'Review Comments'
      },
      {
        id: 3,
        type: 'competitor_activity',
        severity: 'low',
        title: 'Competitor Activity',
        message: `TravelCorp launched a new Tamil Nadu campaign with similar hashtags. Monitor their engagement and adjust strategy.`,
        timestamp: new Date(Date.now() - 1800000),
        isRead: true,
        platform: 'all',
        metrics: { overlap: '67%', engagement: '8.2%' },
        action: 'Analyze Campaign'
      },
      {
        id: 4,
        type: 'milestone',
        severity: 'high',
        title: 'Milestone Achievement',
        message: `Congratulations! You've reached 50K followers on Instagram. Your growth rate is 25% above industry average.`,
        timestamp: new Date(Date.now() - 3600000),
        isRead: false,
        platform: 'instagram',
        metrics: { followers: '50K', growth: '+25%' },
        action: 'Celebrate'
      },
      {
        id: 5,
        type: 'content_opportunity',severity: 'medium',title: 'Content Opportunity',
        message: `#MonsoonTravel is trending with 45% growth. Consider creating monsoon-themed content for Tamil Nadu destinations.`,
        timestamp: new Date(Date.now() - 7200000),
        isRead: true,
        platform: 'all',
        metrics: { trend: '+45%', potential: 'High' },
        action: 'Create Content'
      }
    ];

    setAlerts(mockAlerts);
    setUnreadCount(mockAlerts.filter(alert => !alert.isRead).length);
  }, []);

  const getAlertIcon = (type) => {
    const icons = {
      engagement_spike: 'TrendingUp',
      negative_sentiment: 'AlertTriangle',
      competitor_activity: 'Eye',
      milestone: 'Award',
      content_opportunity: 'Lightbulb'
    };
    return icons[type] || 'Bell';
  };

  const getAlertColor = (severity) => {
    const colors = {
      high: 'text-error',
      medium: 'text-warning',
      low: 'text-secondary'
    };
    return colors[severity] || 'text-muted-foreground';
  };

  const getAlertBgColor = (severity) => {
    const colors = {
      high: 'bg-error/10 border-error/20',
      medium: 'bg-warning/10 border-warning/20',
      low: 'bg-secondary/10 border-secondary/20'
    };
    return colors[severity] || 'bg-muted/10 border-border';
  };

  const getPlatformIcon = (platform) => {
    const icons = {
      instagram: 'Instagram',
      twitter: 'Twitter',
      facebook: 'Facebook',
      all: 'Globe'
    };
    return icons[platform] || 'Globe';
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return timestamp.toLocaleDateString();
  };

  const handleMarkAsRead = (alertId) => {
    setAlerts(prev => prev.map(alert => 
      alert.id === alertId ? { ...alert, isRead: true } : alert
    ));
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const handleMarkAllAsRead = () => {
    setAlerts(prev => prev.map(alert => ({ ...alert, isRead: true })));
    setUnreadCount(0);
  };

  const handleAlertAction = (alert) => {
    console.log(`Executing action: ${alert.action} for alert: ${alert.title}`);
    // Implement specific actions based on alert type
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 shadow-soft">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
          {unreadCount > 0 && (
            <span className="bg-error text-error-foreground text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {unreadCount}
            </span>
          )}
        </div>
        
        {unreadCount > 0 && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleMarkAllAsRead}
          >
            Mark All Read
          </Button>
        )}
      </div>

      <div className="space-y-4 max-h-96 overflow-y-auto">
        {alerts.map((alert) => (
          <div 
            key={alert.id} 
            className={`border rounded-lg p-4 transition-micro ${
              alert.isRead ? 'bg-background' : 'bg-muted/30'
            } ${getAlertBgColor(alert.severity)}`}
          >
            <div className="flex items-start space-x-3">
              <div className={`p-2 rounded-lg ${getAlertColor(alert.severity)} bg-current/10`}>
                <Icon 
                  name={getAlertIcon(alert.type)} 
                  size={18} 
                  className={getAlertColor(alert.severity)}
                />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center space-x-2">
                    <h4 className="font-medium text-foreground text-sm">{alert.title}</h4>
                    {!alert.isRead && (
                      <div className="w-2 h-2 bg-primary rounded-full" />
                    )}
                  </div>
                  
                  <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                    <Icon 
                      name={getPlatformIcon(alert.platform)} 
                      size={14} 
                    />
                    <span>{formatTimestamp(alert.timestamp)}</span>
                  </div>
                </div>
                
                <p className="text-sm text-foreground mb-3 leading-relaxed">
                  {alert.message}
                </p>
                
                {alert.metrics && (
                  <div className="flex items-center space-x-4 mb-3 text-xs">
                    {Object.entries(alert.metrics).map(([key, value]) => (
                      <div key={key} className="flex items-center space-x-1">
                        <span className="text-muted-foreground capitalize">{key}:</span>
                        <span className="font-medium text-foreground">{value}</span>
                      </div>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleAlertAction(alert)}
                  >
                    {alert.action}
                  </Button>
                  
                  {!alert.isRead && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleMarkAsRead(alert.id)}
                    >
                      Mark as Read
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {alerts.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Bell" size={48} className="text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No alerts at the moment</p>
          <p className="text-sm text-muted-foreground mt-1">
            We'll notify you of important changes and opportunities
          </p>
        </div>
      )}
    </div>
  );
};

export default AlertsPanel;